import React, {
  Dispatch, FC, ReactElement, SetStateAction, useRef, useState,
} from 'react';
import {
  FlatList, StyleSheet, Text, TouchableOpacity, Modal, FlexStyle, ActivityIndicator,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../constants/Colors';
import { Area, Floor } from './Map/SetFloorsModal/FloorItem';
import titleCase from './TitleCase';
import { AssetInstance } from '../constants/Types';

interface DropdownSelectorProps {
  label?: string;
  data: (Floor | Area | AssetInstance)[];
  selected: Floor | Area | AssetInstance;
  setSelected: Dispatch<SetStateAction<Floor | Area | AssetInstance>>;
  style?: FlexStyle;
  disabled?: boolean,
}

const DropdownSelector: FC<DropdownSelectorProps> = ({
  label, data, selected, setSelected, style, disabled,
}) => {
  const DropdownButton = useRef();
  const [visible, setVisible] = useState<boolean>(false);
  const [dropdownTop, setDropdownTop] = useState<number>(0);
  const [dropdownLeft, setDropdownLeft] = useState<number>(0);
  const [dropdownWidth, setDropdownWidth] = useState<number>(0);

  const toggleDropdown = (): void => (visible ? setVisible(false) : openDropdown());

  const openDropdown = (): void => {
    // @ts-ignore
    DropdownButton?.current?.measure(
      (_fx: number, _fy: number, w: number, h: number, px: number, py: number) => {
        setDropdownTop(py + h + 10);
        setDropdownLeft(px);
        setDropdownWidth(w);
      },
    );

    setVisible(true);
  };

  const onItemPress = (item: Floor | Area | AssetInstance): void => {
    setSelected(item);
    setVisible(false);
  };

  const renderItem = (item: Floor | Area | AssetInstance):
    ReactElement<any, any> => {
    // @ts-ignore
    const isSelected: boolean = (!!item.Name && item.Name === selected?.Name)
      // @ts-ignore
      || (!!item.name && item.name === selected?.name);
    return <TouchableOpacity
      style={[styles.item, isSelected && styles.selectedOption]}
      onPress={() => onItemPress(item)}
    >
      <Text style={[styles.text, isSelected && styles.selectedText]}>
        {/* @ts-ignore */}
        {item.Name ?? item.name}
      </Text>
    </TouchableOpacity>;
  };

  const renderDropdown = (): ReactElement<any, any> => (
    <Modal
      {...{ visible }}
      transparent
      animationType='fade'
    >
      <TouchableOpacity onPress={() => setVisible(false)}>
        <FlatList
          style={[styles.dropdown, { top: dropdownTop, left: dropdownLeft, width: dropdownWidth }]}
          data={data}
          renderItem={({ item }) => renderItem(item)}
          keyExtractor={(_, index: number) => `env_location_dropdown_item_${index}`}
        />
      </TouchableOpacity>
    </Modal>
  );

  return <TouchableOpacity
    ref={DropdownButton}
    style={[styles.button, disabled && styles.buttonDisabled, style]}
    disabled={!(data?.length > 1)}
    onPress={toggleDropdown}
    {...{ disabled }}
  >
    {renderDropdown()}
    {disabled ? <Text style={styles.text}>{'N/A'}</Text> : <>
      <Text style={styles.text}>
        {/* @ts-ignore */}
        {data?.length > 1 && (selected?.Name ?? selected?.name ?? `All ${label}s`)}
        {/* @ts-ignore */}
        {data?.length === 1 && (data[0]?.Name ?? data[0]?.name)}
        {data?.length === 0 && `No ${label}s available`}
        {data === undefined && titleCase(`${label} list unavailable`)}
        {data === null && titleCase(`Loading list of ${label}s`)}
      </Text>
      {data?.length > 1 && <FontAwesome
        size={25}
        style={styles.icon}
        name={visible ? 'angle-up' : 'angle-down'}
      />}
      {data === null && <ActivityIndicator
        size='small'
        color={styles.icon.color}
      />}
    </>}
  </TouchableOpacity>;
};

const styles = StyleSheet.create({
  item: {
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  selectedOption: {
    backgroundColor: '#003A70',
  },
  selectedText: {
    color: 'white',
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 4,
    minHeight: 40,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  text: {
    flex: 1,
    color: Colors.light.hereworksBlack600,
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Poppins_400Regular',
  },
  dropdown: {
    backgroundColor: '#fff',
    flex: 1,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 12,
    maxHeight: 200,
  },
  icon: {
    color: Colors.light.hereworksBlue200,
    marginBottom: 3,
  },
});

export default DropdownSelector;
